import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { photos, categories } from '../data/mock';
import { Lightbox } from '../components/Lightbox';
import { usePageMeta } from '../hooks/use-page-meta';

const isValidCategory = (id) => categories.some(c => c.id === id);

export const GalleryPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const categoryParam = searchParams.get('category');

  useEffect(() => {
    if (categoryParam && isValidCategory(categoryParam)) {
      setSelectedCategory(categoryParam);
    }
  }, [categoryParam]);

  const filteredPhotos = selectedCategory === 'all' 
    ? photos 
    : photos.filter(photo => photo.category === selectedCategory);

  const categoryName = categories.find(c => c.id === selectedCategory)?.name;
  const isAll = selectedCategory === 'all';
  usePageMeta({
    title: isAll ? 'Portfolio' : `${categoryName} Photography Dubai`,
    description: isAll
      ? 'Browse the full portfolio of Elina K. Photography: family, newborn, maternity and couples photo sessions in Dubai.'
      : `${categoryName} photography in Dubai by Elina K. Browse the ${categoryName} collection and book your own session.`,
    path: isAll ? '/gallery' : `/gallery?category=${selectedCategory}`,
    image: filteredPhotos[0]?.image
  });

  return (
    <div className="min-h-screen bg-[#1a1a1a]" style={{ paddingTop: '100px' }}>
      <div className="revolt-container section-spacing">
        {/* Header */}
        <div className="text-center mb-lg">
          <h1 className="section-title">{isAll ? 'Portfolio' : categoryName}</h1>
          <p className="subtitle">{isAll ? 'Browse our complete collection' : `${categoryName} photography in Dubai`}</p>
        </div>

        {/* Category Filter */}
        <div className="flex justify-center gap-4 mb-lg flex-wrap">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-3 text-sm font-light tracking-widest uppercase transition-all duration-300 border ${
                selectedCategory === category.id 
                  ? 'border-[#c9a87c] text-[#c9a87c] bg-[#c9a87c]/10' 
                  : 'border-white/20 text-white/60 hover:border-white/40 hover:text-white'
              }`}
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Results Count */}
        <div className="mb-8 text-center">
          <p className="text-[#888] text-sm tracking-wide">
            {filteredPhotos.length} {filteredPhotos.length === 1 ? 'photo' : 'photos'}
          </p>
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPhotos.map((photo, index) => (
            <div 
              key={photo.id} 
              className="portfolio-card fade-in-up"
              style={{ animationDelay: `${index * 0.05}s` }}
              onClick={() => setLightboxIndex(index)}
              data-testid={`gallery-photo-${photo.id}`}
            >
              <img 
                src={photo.image} 
                alt={`${photo.title} – ${categories.find(c => c.id === photo.category)?.name} photography in Dubai by Elina K.`}
                loading={index < 3 ? 'eager' : 'lazy'}
                decoding="async"
              />
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredPhotos.length === 0 && (
          <div className="text-center py-20">
            <p className="text-[#888]">No photos found in this category.</p>
          </div>
        )}
      </div>

      <Lightbox
        photos={filteredPhotos}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />
    </div>
  );
};
